import { Stack, Text } from "@mantine/core"
import { useTranslation } from "react-i18next"
import { DateTime } from "@/components/utils"
import { useActivityEnabled, useAPIResources } from "@/hooks"
import type { TUser, TVacationRequest } from "@/types"
import { dayjs, vacationRequestAPI } from "@/utils"
import { Panel } from "./utils"

const delta = 7

export function vacationStatus(requests: TVacationRequest[], today: dayjs.Dayjs) {
  const day = today.startOf("day")

  const current = requests.find((request) => {
    const start = dayjs(request.dtstart).startOf("day")
    const end = dayjs(request.dtend).startOf("day")
    return !start.isAfter(day) && !end.isBefore(day)
  })
  if (current) return { current }

  const upcoming = requests.find((request) => {
    const days = dayjs(request.dtstart).startOf("day").diff(day, "day")
    return days > 0 && days <= delta
  })
  if (!upcoming) return null

  return { upcoming, days: dayjs(upcoming.dtstart).startOf("day").diff(day, "day") }
}

function DateRange({ request }: { request: TVacationRequest }) {
  return (
    <>
      <DateTime timestamp={request.dtstart} format="DD MMM" /> —{" "}
      <DateTime timestamp={request.dtend} format="DD MMM YYYY" />
    </>
  )
}

export function ProfileVacation({ user }: { user: TUser }) {
  const { t } = useTranslation()
  const enabled = useActivityEnabled("vacation")
  const { data: { data: requests = [] } = {}, isLoading } = useAPIResources(vacationRequestAPI, {
    enabled,
    where: { user_id: user.id, status: "approved" },
    sort: ["dtstart"],
  })

  if (isLoading) return null

  const status = vacationStatus(requests, dayjs())
  if (!status) return null

  return (
    <Panel>
      <Stack p="md" align="center" justify="center" gap="sm" data-testid="vacation">
        <Text fz={24} c="dimmed">
          {status.current ? t("On vacation") : t("Going on vacation soon")}
        </Text>
        <Text fz={100} lh="100px">
          🌴
        </Text>
        {status.current ? (
          <Text fz={20} fw={500}>
            <DateRange request={status.current} />
          </Text>
        ) : (
          <>
            <Text fz={24} c="red" fw={500}>
              {status.days === 1 ? t("Tomorrow") : dayjs().startOf("day").add(status.days, "day").fromNow()}
            </Text>
            <Text fz={20} fw={500}>
              <DateRange request={status.upcoming} />
            </Text>
          </>
        )}
      </Stack>
    </Panel>
  )
}
